import { CircularProgress, InputAdornment, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { ErrorMessage, Form, Formik } from 'formik'
import EmployeeList from 'module/payroll/global-components/EmployeeList'
import React from 'react'
import * as yup from 'yup'

const moneyValidation = yup
  .string()
  .nullable()
  .required('This field is required')
  .matches(
    /(^\d{1,3}(,\d{3})*(\.[0-9]{1,2})*$)|(^[0-9]*(\.[0-9]{1,2})*$)/,
    'This field only accept a valid number format'
  )

const validationSchema = yup.object().shape({
  epf_employee: moneyValidation,
  epf_employer: moneyValidation,
  socso_employee: moneyValidation,
  socso_employer: moneyValidation,
  skbbk_employee: moneyValidation,
  eis_employee: moneyValidation,
  eis_employer: moneyValidation,
  mtd_current: moneyValidation,
  mtd_remarks: yup.string().nullable(),
})

const buildInitialValues = (data, values) => ({
  epf_employee: values?.epf?.employee ?? data?.epf?.employee ?? '',
  epf_employer: values?.epf?.employer ?? data?.epf?.employer ?? '',
  socso_employee: values?.socso?.employee ?? data?.socso?.employee ?? '',
  socso_employer: values?.socso?.employer ?? data?.socso?.employer ?? '',
  skbbk_employee: values?.skbbk?.employee ?? data?.skbbk?.employee ?? '',
  eis_employee: values?.eis?.employee ?? data?.eis?.employee ?? '',
  eis_employer: values?.eis?.employer ?? data?.eis?.employer ?? '',
  mtd_current: values?.mtd?.current ?? data?.mtd?.nett_mtd ?? '',
  mtd_remarks: values?.mtd?.remarks ?? data?.mtd?.mtd_remarks ?? '',
})

const sanitizeMoney = (value) => `${value ?? ''}`.replace(/,/g, '').trim()

const ManualStatutoryAdjustmentModal = ({
  open,
  handleClose,
  data,
  values,
  onApply,
  isLoading = false,
}) => {
  return (
    <AdxFormModal
      title="Edit statutory contribution"
      subtitle=""
      handleClose={handleClose}
      open={open}
      modalWidth={640}
    >
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={buildInitialValues(data, values)}
        onSubmit={(formValues) => {
          onApply?.({
            epf: {
              employee: sanitizeMoney(formValues.epf_employee),
              employer: sanitizeMoney(formValues.epf_employer),
            },
            socso: {
              employee: sanitizeMoney(formValues.socso_employee),
              employer: sanitizeMoney(formValues.socso_employer),
            },
            skbbk: {
              employee: sanitizeMoney(formValues.skbbk_employee),
            },
            eis: {
              employee: sanitizeMoney(formValues.eis_employee),
              employer: sanitizeMoney(formValues.eis_employer),
            },
            mtd: {
              current: sanitizeMoney(formValues.mtd_current),
              remarks: formValues.mtd_remarks?.trim?.() || '',
            },
          })
        }}
      >
        {({ values, handleChange, handleBlur, touched, errors, handleSubmit }) => {
          const renderMoneyInput = (name, label) => (
            <AdxInput
              name={name}
              label={label}
              value={values[name]}
              onChange={handleChange}
              onBlur={handleBlur}
              startAdornment={<InputAdornment position="start">RM</InputAdornment>}
              errorMessage={<ErrorMessage name={name} component={AdxErrorMessage} />}
              isErrorShown={touched[name] && errors[name]}
            />
          )

          return (
            <Form>
              <Typography color="light-gray-text.main" sx={{ mb: 2 }}>
                Update statutory contribution values before recalculating or submitting payroll.
              </Typography>

              <EmployeeList avatar={data?.avatar} name={data?.name} position={data?.position} />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5 }}>
                EPF
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {renderMoneyInput('epf_employee', 'Employee contribution')}
                {renderMoneyInput('epf_employer', 'Employer contribution')}
              </div>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5 }}>
                SOCSO
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {renderMoneyInput('socso_employee', 'Employee contribution')}
                {renderMoneyInput('socso_employer', 'Employer contribution')}
              </div>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5 }}>
                SKBBK
              </Typography>
              {renderMoneyInput('skbbk_employee', 'Employee contribution')}

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5 }}>
                EIS
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {renderMoneyInput('eis_employee', 'Employee contribution')}
                {renderMoneyInput('eis_employer', 'Employer contribution')}
              </div>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5 }}>
                Income tax
              </Typography>
              {renderMoneyInput('mtd_current', 'Current amount')}

              <AdxInput
                name="mtd_remarks"
                multiline
                minRows={3}
                label="Remarks"
                value={values.mtd_remarks}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ mt: 2 }}
                sxInput={{ padding: 2 }}
                errorMessage={<ErrorMessage name="mtd_remarks" component={AdxErrorMessage} />}
                isErrorShown={touched.mtd_remarks && errors.mtd_remarks}
              />

              <AdxButton variant="contained" fullWidth sx={{ mt: 3 }} onClick={handleSubmit}>
                {isLoading ? <CircularProgress color="white" size={20} /> : 'Apply'}
              </AdxButton>
            </Form>
          )
        }}
      </Formik>
    </AdxFormModal>
  )
}

export default ManualStatutoryAdjustmentModal
